/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the table of finished and collecting runs.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api ClassDetail
 * @side-effects opens the run review screen
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { StatusText } from '../../../shared/components/StatusIcon';
import { tableCells, VTable } from '../../../shared/components/VTable';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { classRuns, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function ClassReviewTab({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const openItem = useWorkspaceUiStore((state) => state.openItem);

  const rows = classRuns(runs, classIndex).filter(
    (run) => run.status === 'ended' || run.status === 'open',
  );

  return (
    <VTable
      className="classroom-mobile-list"
      empty={t('還沒有結束的活動。')}
      columns={[
        { heading: t('上課'), mobileLabel: '上課' },
        { heading: t('方式'), mobileLabel: '方式' },
        { heading: t('人數'), mobileLabel: '人數' },
        { heading: t('報告'), mobileLabel: '報告' },
      ]}
      rows={rows.map((run) => ({
        key: run.id,
        onSelect: () => openItem({ k: 'rev', id: run.id }),
        cells: tableCells(
          <span className="vnmc">
            <span className="vnic">{t('回')}</span>
            <b>{t(run.dateLabel)}</b>
            <span className="vmut" style={{ marginLeft: 8 }}>
              {runName(activities, run)}
            </span>
          </span>,
          <span className="vmut">{t(RUN_MODE_LABEL[run.mode])}</span>,
          <span className="vmut">{`${classes[classIndex].students.length} 人`}</span>,
          run.sent ? (
            <StatusText glyph="done" label={t('已發送')} />
          ) : (
            <StatusText glyph="warn" label={t('待審')} tone="wn" />
          ),
        ),
      }))}
      template="minmax(0,1fr) 110px 80px 120px"
    />
  );
}
